import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import {
  ELT_FORM_SECTION,
  IELTFormData,
  TComplexSectionData,
  TEltFormData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { getShortTermData } from 'legacy/lut/components/widgets/public/partner360/ELT/api/shortTermData/getShortTermData'
import { scoreCardTabNumber } from 'legacy/lut/components/widgets/public/partner360/ELT/configs'
import { eltEventBus } from 'legacy/lut/components/widgets/public/partner360/ELT/eventBus/eltEventBus'
import { getComplexForceUpdateData } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/forceUpdate/getComplexForceUpdateData'
import { getFinancialDataHelper } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/requestsHelpers/getFinancialDataHelper'
import { TForceUpdateConfig } from 'legacy/lut/components/widgets/public/partner360/ELT/models/TForceUpdateConfig'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { Dispatch, SetStateAction, useCallback } from 'react'

import { fetchEltPlanningData } from 'partner360/services/planning/fetchEltPlanningData'

import { fetchELTPopulatedData } from '../api/eltData/fetchELTPopulatedData'
import { TUpdateELTFormData } from '../models/common'

interface IProps {
  onChangeComplexFormData: (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => void
  updateData: TUpdateELTFormData
  groupId: number | null
  partnerId: string
  fYear?: number
  eltType?: EltType
  setWip: Dispatch<SetStateAction<boolean>>
  dataForSave: TEltFormData
  forceUpdateFullForm: (newYear: number, newQuarter: number) => void
  currentStep?: number
  uid?: string
}

export const useForceUpdate = ({
  onChangeComplexFormData,
  groupId,
  partnerId,
  fYear,
  setWip,
  dataForSave,
  updateData,
  forceUpdateFullForm,
  eltType = EltType.Pptx,
  currentStep,
  uid,
}: IProps) => {
  const {
    jbpSettings: { fQuarterELT: quarterForSave, fYearELT: yearForSave },
  } = useJbpSettings()

  const forceUpdate = useCallback(
    async (section: ELT_FORM_SECTION, config?: TForceUpdateConfig) => {
      if (fYear) {
        setWip(true)
        if (yearForSave !== fYear) {
          await forceUpdateFullForm(yearForSave, quarterForSave)
          setWip(false)
          return
        }

        const populatedData = await fetchELTPopulatedData({ eltType, partnerId, groupId, fYear })
        const markets = populatedData?.Markets.split(';') || []

        const financialData = await getFinancialDataHelper({
          partnerId,
          fYear,
          groupId: groupId,
          forceFetch: true,
          eltType,
          isRollup: dataForSave.IsRollup || 0,
        })
        setWip(false)
        if (financialData) {
          const res = getComplexForceUpdateData({
            financialData,
            section,
            config,
            dataForSave,
            markets,
            eltType,
          })
          if (res === 'NOT_FOUND') {
            return
          }

          onChangeComplexFormData(section)(res)
        }
        updateData('FQuarter', quarterForSave)
      }
    },
    [
      eltType,
      fYear,
      setWip,
      yearForSave,
      partnerId,
      groupId,
      forceUpdateFullForm,
      quarterForSave,
      updateData,
      dataForSave,
      onChangeComplexFormData,
    ]
  )

  const forceUpdateFormToActualData = useCallback(async () => {
    if (fYear) {
      setWip(true)
      await forceUpdateFullForm(yearForSave, quarterForSave)
      if (eltType === EltType.Pptx && !(dataForSave as IELTFormData).LastMeetingData) {
        await onChangeComplexFormData(ELT_FORM_SECTION.LAST_MEETING)([])
      }
      if (currentStep === scoreCardTabNumber) {
        eltEventBus.notify('updateScoreCardData')
      } else if (uid) {
        const req1 = fetchEltPlanningData(uid, true)
        const req2 = getShortTermData(uid, true)
        await Promise.all([req1, req2])
      }
      setWip(false)
    }
  }, [
    fYear,
    setWip,
    forceUpdateFullForm,
    yearForSave,
    quarterForSave,
    eltType,
    dataForSave,
    onChangeComplexFormData,
    currentStep,
    uid,
  ])

  return { forceUpdate, forceUpdateFormToActualData }
}
